/**
 * PortalShell — portal surface app shell.
 *
 * Composes AppShell + Topbar + Sidebar with the portal nav and wires the
 * DisplaySettingsPanel open/close state to the
 * Topbar's display-settings button.
 *
 * Presentational surface wrapper:
 *   - props in → UI out
 *   - no data fetching, no privileged logic, no secrets
 *   - safe for Inertia portal bundle
 *
 * a11y:
 *   - Topbar renders a <header> landmark
 *   - Sidebar renders a <nav> landmark
 *   - AppShell renders a <main> landmark
 *   - DisplaySettingsPanel has role=dialog (via its own markup)
 *   - all interactive elements are native <button>/<a> elements
 */
import React, { createContext, useContext, useState } from 'react'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { useLocale, useT } from '@/shared/i18n'
import { LocaleSwitcher } from '@/shared/i18n/LocaleSwitcher'
import { AppShell } from './AppShell'
import { Topbar } from './Topbar'
import { Sidebar, type SidebarGroup } from './Sidebar'
import { DisplaySettingsPanel } from './DisplaySettingsPanel'
import { SlideOver } from '@/shared/components/SlideOver'
import { FeedbackWidget } from '@/shared/components/FeedbackWidget'
import { UserMenu, type UserMenuItem } from '@/shared/components/UserMenu'
import { Icon, type IconName } from '@/shared/components/Icon'
import { ButtonLink, TextLink } from '@/shared/components'
import { useDisplayPreferences } from '@/shared/theme/useDisplayPreferences'
import brandMark from '@/assets/brand/mesternemnda.svg'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PortalShellProps {
  children: React.ReactNode
  /**
   * Page title. Rendered into <title> via Inertia's <Head>, which the
   * entrypoint's title callback suffixes with the product name. Typed optional
   * so the shell stays renderable in isolation; the `page-title` vitest gate is
   * what actually requires every page to pass one.
   */
  title?: string
  /** Explicit unread notification count. Falls back to Inertia shared `unread_count`. */
  unreadCount?: number
}

// The portal entrypoint supplies its interactive bell; shared chrome owns no
// notification transport or recipient-specific data.
export const PortalBellContext = createContext<React.ReactNode>(null)

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PortalShell({ children, title, unreadCount }: PortalShellProps) {
  const notificationBell = useContext(PortalBellContext)
  const t = useT()
  const locale = useLocale()
  const { prefs, set, reset } = useDisplayPreferences()

  // Resolve unread count: explicit prop wins over Inertia shared value.
  // The signed-in applicant's name is shared by Portal::AuthenticatedController;
  // it is absent on anonymous pages (Portal::BaseController) — render no chip.
  const { unread_count: sharedUnreadCount, applicant } = usePage().props as {
    unread_count?: number
    applicant?: { display_name: string }
  }
  const resolved = typeof unreadCount === 'number' ? unreadCount : sharedUnreadCount
  const hasUnread = typeof resolved === 'number' && resolved > 0

  // Open/close state for the display settings panel.
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false)

  // Open/close state for the mobile navigation drawer. The hamburger button
  // (rendered in the Topbar, visible only at the phone breakpoint via CSS)
  // toggles this; the Sidebar is rendered inside a left-anchored SlideOver so
  // it slides in over the content instead of squeezing it into a fixed column.
  const [navDrawerOpen, setNavDrawerOpen] = useState(false)

  // ---------------------------------------------------------------------------
  // Sidebar nav groups
  // ---------------------------------------------------------------------------

  // The primary nav is applicant-only. Signed-out portal pages (BaseController,
  // no `applicant`) render no nav items — showing Min side / Mine søknader to a
  // signed-out visitor is misleading (and "Min side" even reads as active). The
  // routes are already Pundit-gated server-side; this is UX gating only.
  const sidebarGroups: SidebarGroup[] = applicant
    ? [
    {
      label: t('portal.nav.home'),
      items: [
        {
          key: 'min-side',
          label: t('portal.nav.account'),
          icon: 'user',
          href: '/min-side',
        },
        {
          key: 'applications',
          label: t('portal.applications.nav.my_applications'),
          icon: 'doc',
          href: '/applications',
        },
        {
          key: 'documents',
          label: t('portal.nav.documents'),
          icon: 'folder',
          href: '/dokumenter',
        },
        {
          key: 'notifications',
          label: t('portal.nav.notifications'),
          icon: 'bell',
          href: '/notifications',
        },
        {
          key: 'company-membership',
          label: t('portal.nav.company_membership'),
          icon: 'building',
          href: '/firmatilknytning/new',
        },
      ],
    },
      ]
    : []

  // Derive the active sidebar key from the current path (best-effort;
  // Inertia pages can override by passing unreadCount or other hints).
  // We read `window.location` safely — on server/test it may not exist.
  // Default to undefined so a path that matches no nav prefix highlights
  // nothing rather than falsely highlighting "Min side".
  let activeKey: string | undefined
  try {
    const path = window.location.pathname
    if (path.startsWith('/applications')) activeKey = 'applications'
    else if (path.startsWith('/dokumenter')) activeKey = 'documents'
    else if (path.startsWith('/notifications')) activeKey = 'notifications'
    else if (path.startsWith('/firmatilknytning')) activeKey = 'company-membership'
    else if (path === '/' || path.startsWith('/min-side')) activeKey = 'min-side'
  } catch {
    // Safe default on non-browser environments.
  }

  // ---------------------------------------------------------------------------
  // Brand slot
  // ---------------------------------------------------------------------------

  // Brand + Min side share the topbar's left side: the Mesternemnda lockup
  // (which carries the name itself — no wordmark text), then the link. No
  // underline on the active item — colour + weight carry it. (Shared layout
  // layer; inline token styles.)
  const brandNavItems = sidebarGroups[0]?.items.filter((item) => item.key === 'min-side') ?? []
  const brand = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <Link
        href="/"
        className="topbar__brand-link"
        aria-label={t('app.name')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}
      >
        <img src={brandMark} className="topbar__brand-logo" alt="" />
      </Link>
      <nav className="topbar__brand-nav" aria-label={t('chrome.nav.label')} style={{ display: 'flex', gap: 4 }}>
        {brandNavItems.map((item) => {
          const isActive = (activeKey ?? '') === item.key
          return (
            <TextLink key={item.key} asChild><Link
              href={item.href}
              aria-current={isActive ? 'page' : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 12px',
                height: 44,
                whiteSpace: 'nowrap',
              }}
            >
              <Icon name={item.icon as IconName} size={16} aria-hidden="true" />
              {item.label}
              {item.count !== undefined && <span>({item.count})</span>}
            </Link></TextLink>
          )
        })}
      </nav>
    </div>
  )

  // ---------------------------------------------------------------------------
  // Actions slot — bell (unread in-app notifications) + locale switcher.
  // ---------------------------------------------------------------------------

  const bellCount = typeof resolved === 'number' ? resolved : 0
  const actionsSlot = (
    <>
      {applicant ? notificationBell ?? (
        <ButtonLink asChild variant="tertiary"><Link
          href="/notifications"
          className="topbar__bell"
          aria-label={t('portal.nav.bell_label', { count: bellCount })}
          data-testid="bell"
        >
          <Icon name="bell" size={18} aria-hidden="true" />
          {bellCount > 0 ? <span className="topbar__bell-count" aria-hidden="true">{bellCount}</span> : null}
        </Link></ButtonLink>
      ) : null}
      <LocaleSwitcher />
    </>
  )

  // ---------------------------------------------------------------------------
  // User slot — signed-in applicant identity chip. The display name is shared
  // by Portal::AuthenticatedController; anonymous pages (Portal::BaseController)
  // omit it, so the chip is suppressed entirely (no crash, no empty chip).
  // ---------------------------------------------------------------------------

  const userMenuItems: UserMenuItem[] = [
    {
      key: 'profile',
      label: t('portal.dashboard.tasks.profile'),
      icon: 'user',
      href: '/min-side/opplysninger',
    },
    {
      key: 'logout',
      label: t('chrome.topbar.sign_out'),
      icon: 'x',
      danger: true,
      separatorBefore: true,
      onSelect: () => router.delete('/session'),
    },
  ]

  const userSlot = applicant ? (
    <div data-testid="identity-chip" className="topbar__user-wrap">
      <UserMenu
        name={applicant.display_name}
        items={userMenuItems}
        triggerLabel={t('chrome.topbar.user_menu_label')}
      />
    </div>
  ) : null

  // ---------------------------------------------------------------------------
  // Render slots
  // ---------------------------------------------------------------------------

  // Sidebar nav, reused in two places: the fixed grid column (desktop) and the
  // off-canvas SlideOver drawer (mobile). Same component, same props. An empty
  // string matches no item, so an unrecognised path highlights nothing.
  const sidebarNav = <Sidebar groups={sidebarGroups} activeKey={activeKey ?? ''} />

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <>
      {title ? <Head title={title} /> : null}
      <AppShell
        navLayout="top"
        responsive
        lang={locale}
        skipToContentLabel={t('common.skip_to_content')}
        topbar={
          <Topbar
            brand={brand}
            actions={actionsSlot}
            user={userSlot}
            onMenuClick={() => setNavDrawerOpen(true)}
            menuButtonLabel={t('chrome.sidebar.expand')}
            onOpenDisplaySettings={() => setDisplaySettingsOpen((o) => !o)}
            displaySettingsLabel={t('chrome.topbar.display_settings_readability')}
            displaySettingsPanel={
              displaySettingsOpen && (
                <DisplaySettingsPanel
                  prefs={prefs}
                  onChange={(key, value) => set(key, value as never)}
                  onReset={reset}
                  onClose={() => setDisplaySettingsOpen(false)}
                  // Theme, palette and typeface are fixed on the public
                  // surface, so their controls would promise a change this
                  // shell cannot deliver. Reading comfort — text size,
                  // density, contrast, crisp, motion — stays fully offered.
                  showTheme={false}
                  showAccent={false}
                  showFont={false}
                />
              )
            }
          />
        }
        sidebar={<div className="app__nav-spacer" aria-hidden="true" />}
      >
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: 1, width: '100%', maxWidth: 1120, margin: '0 auto' }}>
            {children}
          </div>
        {/* Public information uses Inertia; contact opens the published mail address. */}
        <footer className="portal-footer">
          <div className="portal-footer__inner">
            <span className="portal-footer__org">
              {t('portal.footer.org', { year: new Date().getFullYear() })}
            </span>
            <nav className="portal-footer__links" aria-label={t('portal.footer.nav_label')}>
              <TextLink asChild><Link href="/personvern">
                {t('portal.footer.personvern')}
              </Link></TextLink>
              <TextLink asChild><Link href="/tilgjengelighet">
                {t('portal.footer.tilgjengelighet')}
              </Link></TextLink>
              {/* The address the privacy statement already publishes — a footer
                  link that goes nowhere is worse than no link. */}
              <TextLink href={`mailto:${t('portal.footer.kontakt_email')}`}>
                {t('portal.footer.kontakt')}
              </TextLink>
            </nav>
          </div>
        </footer>
        </div>
      </AppShell>

      {/* Mobile navigation drawer — the same Sidebar nav rendered off-canvas.
          SlideOver traps focus, closes on Escape / backdrop, and restores focus
          to the hamburger on close. Anchored to the left edge. Tapping a nav link
          performs an Inertia visit which remounts this shell, resetting the
          drawer to closed. */}
      <SlideOver
        open={navDrawerOpen}
        onClose={() => setNavDrawerOpen(false)}
        side="left"
        title={t('chrome.nav.label')}
      >
        {sidebarNav}
      </SlideOver>

      {/* Unread-badge hidden span for backwards-compat test expectations.
          This is the live-region announcement; the bell badge in the top
          bar is the visual count. Keeps the data-testid="unread-badge"
          contract from the original PortalShell so consuming tests
          continue to work. */}
      {hasUnread && (
        <span
          data-testid="unread-badge"
          className="sr-only"
          aria-live="polite"
        >
          {t('portal.notifications.unread_badge', { count: resolved as number })}
        </span>
      )}

      <FeedbackWidget />
    </>
  )
}
